import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
	HiChevronDown,
	HiMagnifyingGlass,
	HiOutlineArchiveBox,
} from 'react-icons/hi2';
import { useOutletContext } from 'react-router-dom';

// import PaginationTopTable from '../table/common/PaginationTopTable';
import PaginationBottomTable from '../table/common/PaginationBottomTable';
import { IPaginationTable } from '../table/common/interfaces/IPaginationTable';
import { EVENT_OPTIONS, HaggleProductStatus, PriceRange } from '../utils/enums';
import { isFavouriteProduct } from '../utils/utils';
import ProductCard from './ProductCard';
import ProductUnavailableCard from './ProductUnavailableCard';

import consumerAuthImage from '@/assets/consumer-auth.png';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { INotifications } from '@/interfaces/api/notifications/INotifications';
import { ICollectionProduct } from '@/interfaces/api/product/ICollectionProduct';
import { IHaggleFilters } from '@/interfaces/api/product/IHaggleFilters';
import { FilterStateSideBar } from '@/interfaces/home/IFilterStateSideBar';
import { ProductCardType } from '@/interfaces/notifications/Notifications';
import { haggleApiService } from '@/services/haggle.service';

type ProductListProps = {
	collectionProduct: ICollectionProduct;
	paginationOption: IPaginationTable;
	favouriteMerchants?: INotifications[];
	type?: ProductCardType;
	refetchFavourites?: () => void;
};

const ProductList = ({
	collectionProduct,
	paginationOption,
	favouriteMerchants,
	type = ProductCardType.Store,
	refetchFavourites,
}: ProductListProps) => {
	const productsToShow = collectionProduct.data || [];
	const [selectedProduct, setSelectedProduct] = useState('Product');
	// const [sortBy, setSortBy] = useState('recommended');

	// Get filters from outlet context
	const context = useOutletContext<{
		filters: FilterStateSideBar;
		setFilters?: (filters: FilterStateSideBar) => void;
	}>();
	const filters = context?.filters || {
		productTypes: [],
		vendors: [],
		priceRanges: [],
		statuses: [],
	};

	// Fetch stored filters from backend
	const { data: storedFilters } = useQuery<IHaggleFilters>({
		queryKey: [ApiQueryKeys.getAllHaggleStoredFilters],
		queryFn: () => haggleApiService.getAllHaggleStoredFilters(),
		refetchOnWindowFocus: false,
	});

	// Sync dropdown with filter state
	useEffect(() => {
		if (filters.productTypes.length === 1) {
			setSelectedProduct(filters.productTypes[0]);
		} else if (filters.productTypes.length === 0) {
			setSelectedProduct('Product');
		}
	}, [filters.productTypes]);

	// Memoized filtered and sorted products
	const displayProducts = useMemo(() => {
		// Filter products based on selected filters
		const filteredProducts = productsToShow.filter((product) => {
			// Only process products that have the required properties
			if (!('id' in product && 'title' in product)) {
				return false; // Skip unavailable products for filtering
			}

			// Filter by product type
			if (filters.productTypes && filters.productTypes.length > 0) {
				const productType = product.product_type?.toLowerCase();
				if (
					!productType ||
					!filters.productTypes.some(
						(type) => productType === type.toLowerCase(),
					)
				) {
					return false;
				}
			}

			// Filter by vendor
			if (filters.vendors && filters.vendors.length > 0) {
				const vendorName = product.vendor?.toLowerCase();
				if (
					!filters.vendors.some((vendor) =>
						vendorName?.includes(vendor.toLowerCase()),
					)
				) {
					return false;
				}
			}

			// Filter by price range
			if (filters.priceRanges && filters.priceRanges.length > 0) {
				const price = parseFloat(product.variants[0]?.price || '0');
				const priceInRange = filters.priceRanges.some((range) => {
					switch (range) {
						case PriceRange.Under100:
							return price < 100;
						case PriceRange.OneHundredToFiveHundred:
							return price >= 100 && price < 500;
						case PriceRange.FiveHundredToOneThousand:
							return price >= 500 && price < 1000;
						case PriceRange.OverOneThousand:
							return price >= 1000;
						default:
							return false;
					}
				});
				if (!priceInRange) return false;
			}

			// Filter by status
			if (filters.statuses && filters.statuses.length > 0) {
				// If "Events" is selected, show both Live and ComingSoon products
				if (filters.statuses.includes(HaggleProductStatus.Events)) {
					// Show only products that have a haggle event (either Live or Coming Soon)
					// Filter out products without haggle events
					if (!product.haggle) {
						return false;
					}
					// If product has a haggle, show it (regardless of Live or ComingSoon status)
				} else {
					// Apply status filtering for Live and ComingSoon
					const isLive =
						product.haggle && new Date(product.haggle.startDate) <= new Date();
					const status = isLive
						? HaggleProductStatus.Live
						: HaggleProductStatus.ComingSoon;
					if (!filters.statuses.includes(status)) {
						return false;
					}
				}
			}

			return true;
		});

		// Sort products based on selected sort option
		// const sortedProducts = [...filteredProducts].sort((a, b) => {
		// 	// Ensure both products have the required properties
		// 	if (!('id' in a && 'title' in a) || !('id' in b && 'title' in b)) {
		// 		return 0;
		// 	}

		// 	switch (sortBy) {
		// 		case 'price-low-high':
		// 			return (
		// 				parseFloat(a.variants[0]?.price || '0') -
		// 				parseFloat(b.variants[0]?.price || '0')
		// 			);
		// 		case 'price-high-low':
		// 			return (
		// 				parseFloat(b.variants[0]?.price || '0') -
		// 				parseFloat(a.variants[0]?.price || '0')
		// 			);
		// 		case 'newest-first':
		// 			return (
		// 				new Date(b.created_at || '0').getTime() -
		// 				new Date(a.created_at || '0').getTime()
		// 			);
		// 		case 'recommended':
		// 		default:
		// 			// Default order (as they appear in mock data)
		// 			return 0;
		// 	}
		// });

		// Ensure no duplicates by using product ID as unique identifier
		const uniqueFilteredProducts = filteredProducts.filter(
			(product, index, self) => {
				if (!('id' in product)) return false;
				return (
					index === self.findIndex((p) => 'id' in p && p.id === product.id)
				);
			},
		);

		return uniqueFilteredProducts;
		// }, [productsToShow, filters, sortBy]);
	}, [productsToShow, filters]);

	return (
		<div className="w-full min-h-screen bg-gray-50" data-test="product-list">
			{/* Header Banner Section */}
			<div className="px-4 sm:px-6 lg:px-12 pb-4">
				<div className="max-w-7xl mx-auto flex items-center justify-between rounded-2xl glassy-shadow-inset-outward">
					{/* Left side - Text content */}
					<div className="flex-1 p-4 sm:p-6">
						<h1 className="text-2xl sm:text-4xl font-light text-gray-800 mb-2 sm:mb-4 leading-tight">
							WIN YOUR PRICE!
						</h1>
						<p className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-purpleBrand-500">
							Exclusive Deals | Vetted Brands | Premium Products
						</p>
					</div>

					{/* Right side - Illustration */}
					<div className="hidden lg:block flex-shrink-0 me-8">
						<div className="w-56 h-56 items-center justify-center overflow-hidden">
							<img
								src={consumerAuthImage}
								alt="Shopping illustration"
								className="object-cover w-full h-full scale-x-[-1]"
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Filter Section - Hidden on mobile */}
			<div className="hidden lg:block px-4 sm:px-6 lg:px-12 pb-3">
				<div className="max-w-7xl mx-auto">
					<div className="flex flex-col lg:flex-row items-start gap-3">
						{/* Left Side - Product Filter */}
						<div className="flex flex-col gap-2 flex-shrink-0">
							{/* Product Type Dropdown */}
							<div className="relative">
								<select
									value={selectedProduct}
									onChange={(e) => {
										const value = e.target.value;
										setSelectedProduct(value);

										const newFilters = {
											...filters,
											productTypes: value === 'Product' ? [] : [value],
										};

										// Update filters in outlet context if available
										if (context?.setFilters) {
											context.setFilters(newFilters);
										}
									}}
									className="appearance-none rounded-xl px-5 py-2 pr-12 glassy-shadow-inset-inward focus:outline-none focus:ring-2 focus:ring-purpleBrand-500 focus:border-transparent min-w-[200px] text-gray-700 font-medium text-sm"
								>
									<option value="Product">Product</option>
									{storedFilters?.productTypes?.map((type) => (
										<option key={type} value={type}>
											{type || 'Uncategorized'}
										</option>
									))}
								</select>
								<HiChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
							</div>

							{/* Haggle Status Filter */}
							<div className="rounded-xl p-3 glassy-shadow-inset-inward min-w-[200px]">
								<div className="space-y-2">
									{EVENT_OPTIONS.map((status) => (
										<label
											key={status.value}
											className="flex items-center space-x-2 cursor-pointer"
										>
											<input
												type="checkbox"
												checked={
													filters.statuses?.includes(status.value) || false
												}
												onChange={(e) => {
													// Only allow one status to be selected at a time
													const newStatuses = e.target.checked
														? [status.value] // Replace with only the selected status
														: []; // Clear if unchecked

													const newFilters = {
														...filters,
														statuses: newStatuses,
													};

													// Update filters in outlet context if available
													if (context?.setFilters) {
														context.setFilters(newFilters);
													}
												}}
												className="rounded text-purpleBrand-500 w-3 h-3 cursor-pointer"
											/>
											<span className="text-xs text-gray-600">
												{status.label}
											</span>
										</label>
									))}
								</div>
							</div>
						</div>

						{/* Right Side - Search Bar */}
						<div className="flex-1 w-full h-full">
							<div className="relative rounded-xl glassy-shadow-inset-inward p-2.5 h-[130px] flex flex-col justify-between opacity-60">
								{/* Top right - Search Icon and Input */}
								<div className="flex items-start justify-start w-full h-full">
									<HiMagnifyingGlass className="text-gray-400 w-4 h-4 mx-2.5 flex-shrink-0" />
									<textarea
										placeholder="Al-Semitic Contextual Visual product search - Coming Soon"
										className="flex-1 bg-transparent border-none outline-none text-gray-500 placeholder-gray-400 text-xs font-medium resize-none overflow-hidden cursor-not-allowed"
										rows={3}
										disabled
										style={{
											display: '-webkit-box',
											WebkitLineClamp: 3,
											WebkitBoxOrient: 'vertical',
											overflow: 'hidden',
										}}
									/>
								</div>

								{/* Bottom end - Search Button */}
								<div className="flex items-center justify-end">
									<button
										disabled
										className="rounded-2xl m-1.5 px-3 py-1.5 glassy-shadow-inset-inward text-gray-500 font-medium text-xs cursor-not-allowed"
									>
										Search
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Mobile Filters - Only visible on mobile */}
			<div className="lg:hidden px-4 pb-3">
				<div className="max-w-7xl mx-auto">
					<div className="flex flex-col gap-3">
						{/* Product Type Dropdown */}
						<div className="relative">
							<select
								value={selectedProduct}
								onChange={(e) => {
									const value = e.target.value;
									setSelectedProduct(value);

									const newFilters = {
										...filters,
										productTypes: value === 'Product' ? [] : [value],
									};

									// Update filters in outlet context if available
									if (context?.setFilters) {
										context.setFilters(newFilters);
									}
								}}
								className="appearance-none rounded-xl px-5 py-2 pr-12 glassy-shadow-inset-inward focus:outline-none focus:ring-2 focus:ring-purpleBrand-500 focus:border-transparent w-full text-gray-700 font-medium text-sm"
							>
								<option value="Product">Product</option>
								{storedFilters?.productTypes?.map((type) => (
									<option key={type} value={type}>
										{type || 'Uncategorized'}
									</option>
								))}
							</select>
							<HiChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
						</div>

						{/* Haggle Status Filter */}
						<div className="rounded-xl p-3 glassy-shadow-inset-inward w-full">
							<div className="space-y-2">
								{EVENT_OPTIONS.map((status) => (
									<label
										key={status.value}
										className="flex items-center space-x-2 cursor-pointer"
									>
										<input
											type="checkbox"
											checked={
												filters.statuses?.includes(status.value) || false
											}
											onChange={(e) => {
												// Only allow one status to be selected at a time
												const newStatuses = e.target.checked
													? [status.value] // Replace with only the selected status
													: []; // Clear if unchecked

												const newFilters = {
													...filters,
													statuses: newStatuses,
												};

												// Update filters in outlet context if available
												if (context?.setFilters) {
													context.setFilters(newFilters);
												}
											}}
											className="rounded text-purpleBrand-500 w-3 h-3 cursor-pointer"
										/>
										<span className="text-xs text-gray-600">
											{status.label}
										</span>
									</label>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Mobile Search Bar - Only visible on mobile */}
			<div className="lg:hidden px-4 pb-3">
				<div className="max-w-7xl mx-auto">
					<div className="relative rounded-xl glassy-shadow-inset-inward p-2.5 h-[110px] flex flex-col justify-between opacity-60">
						{/* Top - Search Icon and Textarea */}
						<div className="flex items-start justify-start w-full h-full">
							<HiMagnifyingGlass className="text-gray-400 w-4 h-4 mx-2.5 mt-1 flex-shrink-0" />
							<textarea
								placeholder="Al-Semitic Contextual Visual product search - Coming Soon"
								className="flex-1 bg-transparent border-none outline-none text-gray-500 placeholder-gray-400 text-xs font-medium resize-none overflow-hidden cursor-not-allowed"
								rows={3}
								disabled
								style={{
									display: '-webkit-box',
									WebkitLineClamp: 3,
									WebkitBoxOrient: 'vertical',
									overflow: 'hidden',
								}}
							/>
						</div>

						{/* Bottom end - Search Button */}
						<div className="flex items-center justify-end">
							<button
								disabled
								className="rounded-2xl m-1.5 px-3 py-1.5 glassy-shadow-inset-inward text-gray-500 font-medium text-xs cursor-not-allowed"
							>
								Search
							</button>
						</div>
					</div>
				</div>
			</div>

			{/* Product Grid Section */}
			<div className="px-4 sm:px-6 lg:px-12 pb-4">
				<div className="max-w-7xl mx-auto">
					{displayProducts.length > 0 ? (
						<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-6 lg:gap-8">
							{displayProducts.map((product) => {
								const isFavourite = isFavouriteProduct(
									product,
									favouriteMerchants,
								);

								if ('id' in product && 'title' in product) {
									return (
										<div key={product.id} className="relative">
											<ProductCard
												productImageAlt={product.title}
												productImageSrc={product.image?.src}
												productName={product.title}
												productVendor={product.vendor}
												productTradeName={product.tradeName}
												productPrice={product.variants[0].price}
												productHref={product.url ?? ''}
												productHaggle={product.haggle}
												cardType={type}
												isFavourite={isFavourite}
												notificationRequiredInfo={{
													haggleStartDate: product.haggle?.startDate,
													haggleEventId: product.haggle?.id,
													productId: product.id,
													productName: product.title,
													productUrl: product.url ?? '',
													sent: false,
												}}
												refetchFavourites={refetchFavourites}
											/>
										</div>
									);
								} else {
									return (
										<div
											key={product.productId}
											className="relative h-72 sm:h-80"
										>
											<ProductUnavailableCard
												tradeName={product.tradeName}
												productId={product.productId}
											/>
										</div>
									);
								}
							})}
						</div>
					) : (
						<div className="flex flex-col items-center justify-center py-20 text-center">
							<div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6 glassy-shadow-inset-outward">
								<HiOutlineArchiveBox className="w-12 h-12 text-gray-400" />
							</div>
							<h3 className="text-xl font-semibold text-gray-900 mb-3">
								No Products Found
							</h3>
							<p className="text-base text-gray-600 max-w-md px-4">
								We couldn't find any products matching your criteria. Try
								adjusting your filters or check back later for new deals.
							</p>
						</div>
					)}

					{/* Pagination Controls */}
					{paginationOption && displayProducts.length > 0 && (
						<div className="mt-6">
							<PaginationBottomTable {...paginationOption} />
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default ProductList;
